import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('es-MX') : '';

const LIGHT_LABEL = {
  green: { label: 'CUADRADO', cls: 'tk-bold' },
  yellow: { label: 'DIFERENCIA MENOR', cls: 'tk-bold' },
  red: { label: 'DIFERENCIA MAYOR', cls: 'tk-bold' },
};

/**
 * Printable thermal-style cash register report. Hidden on screen, shown only in print.
 * Width auto-adapts to admin-configured 58mm or 80mm.
 *
 * Props:
 *   data: object returned by /api/cash-register/{arqueo|close|logs/{id}}
 *   variant: 'arqueo' | 'close'
 */
export default function CashRegisterTicket({ data, variant }) {
  const { getAuthHeaders } = useAuth();
  const [shop, setShop] = useState({});
  const [width, setWidth] = useState(80);

  useEffect(() => {
    let active = true;
    Promise.all([
      axios.get(`${API}/api/settings/shop_info`, { headers: getAuthHeaders() }).catch(() => ({ data: {} })),
      axios.get(`${API}/api/settings/thermal_paper_width`, { headers: getAuthHeaders() }).catch(() => ({ data: { value: 80 } })),
    ]).then(([s, w]) => {
      if (!active) return;
      setShop(s.data?.value || {});
      setWidth(Number(w.data?.value) === 58 ? 58 : 80);
    });
    return () => { active = false; };
  }, [getAuthHeaders]);

  if (!data) return null;
  const c = data.count || {};
  const sum = data.summary || {};
  const light = LIGHT_LABEL[data.traffic_light] || LIGHT_LABEL.red;
  const isClose = variant === 'close';
  const widthMm = width === 58 ? '52mm' : '74mm';

  return (
    <div className="thermal-ticket" data-testid={`cash-ticket-${variant}`} style={{ width: widthMm }}>
      <style>{`@media print { .thermal-ticket { width: ${widthMm} !important; } }`}</style>
      <div className="tk-center">
        <div className="tk-lg">{shop.name || 'Fixif'}</div>
        {shop.address && <div className="tk-sm">{shop.address}</div>}
        {shop.phone && <div className="tk-sm">Tel: {shop.phone}</div>}
        <div className="tk-sm">Sucursal: {data.branch_name || data.branch_id}</div>
        <div className="tk-lg" style={{ marginTop: 6 }}>
          {isClose ? 'CIERRE DE CAJA' : 'ARQUEO DE CAJA'}
        </div>
      </div>
      <hr className="tk-divider" />
      <div className="tk-sm">
        <div className="tk-row"><span>Fecha</span><span>{fmtDate(data.created_at)}</span></div>
        <div className="tk-row"><span>Apertura</span><span>{fmtDate(data.session_open_at)}</span></div>
        <div className="tk-row"><span>Apert. por</span><span>{data.opened_by}</span></div>
        <div className="tk-row"><span>Cerrado por</span><span>{data.user_name}</span></div>
      </div>
      <hr className="tk-divider" />
      <div className="tk-sm tk-bold">VENTAS DE LA SESION</div>
      <div className="tk-sm">
        <div className="tk-row"><span>Efectivo</span><span>{fmt(sum.cash_sales)}</span></div>
        <div className="tk-row"><span>Tarjeta</span><span>{fmt(sum.card_sales)}</span></div>
        <div className="tk-row"><span>Transferencia</span><span>{fmt(sum.transfer_sales)}</span></div>
        <div className="tk-row tk-bold"><span>Total ventas</span><span>{fmt(sum.total_sales)}</span></div>
        <div className="tk-row"><span>No. operaciones</span><span>{sum.sales_count || 0}</span></div>
        <div className="tk-row"><span>Egresos</span><span>-{fmt(sum.expenses_total)}</span></div>
      </div>
      <hr className="tk-divider" />
      <div className="tk-sm tk-bold">CONTEO</div>
      <table>
        <thead>
          <tr>
            <td className="tk-sm">Denom</td>
            <td className="tk-sm tk-right">Cant</td>
            <td className="tk-sm tk-right">Subtotal</td>
          </tr>
        </thead>
        <tbody>
          {(c.denominations || []).filter(d => d.quantity > 0).map(d => (
            <tr key={d.denom}>
              <td className="tk-sm">${d.denom}</td>
              <td className="tk-sm tk-right">{d.quantity}</td>
              <td className="tk-sm tk-right">{fmt(d.subtotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="tk-row tk-sm">
        <span>Subt. denominaciones</span><span>{fmt(c.denominations_total)}</span>
      </div>
      {(c.dollars || 0) > 0 && (
        <div className="tk-sm">
          <div className="tk-row">
            <span>Dolares</span>
            <span>${(c.dollars || 0).toFixed(2)} USD</span>
          </div>
          <div className="tk-row">
            <span style={{ paddingLeft: 8 }}>x {c.usd_rate}</span>
            <span>{fmt(c.dollars_mxn)}</span>
          </div>
        </div>
      )}
      {(c.vales || []).some(v => v > 0) && (
        <div className="tk-sm">
          {(c.vales || []).map((v, i) => v > 0 && (
            <div key={i} className="tk-row"><span>Vale {i + 1}</span><span>{fmt(v)}</span></div>
          ))}
          <div className="tk-row"><span>Subt. vales</span><span>{fmt(c.vales_total)}</span></div>
        </div>
      )}
      <hr className="tk-divider" />
      <div className="tk-sm">
        <div className="tk-row tk-bold"><span>Total contado</span><span>{fmt(c.grand_total)}</span></div>
        <div className="tk-row"><span>Esperado</span><span>{fmt(data.expected_cash)}</span></div>
        <div className="tk-row tk-bold"><span>Diferencia</span><span>{fmt(data.difference)}</span></div>
        <div className="tk-center" style={{ marginTop: 4 }}>
          <span className={light.cls}>[ {light.label} ]</span>
        </div>
      </div>
      {isClose && (
        <>
          <hr className="tk-divider" />
          <div className="tk-code-box">
            <div className="tk-sm">EFECTIVO A ENTREGAR</div>
            <div className="tk-xl">{fmt(data.deliver_cash)}</div>
            <div className="tk-sm">(Total contado − fondo apertura)</div>
          </div>
        </>
      )}
      <hr className="tk-divider" />
      <div className="tk-center tk-sm">
        <div>Fondo apertura: {fmt(data.opening_fund)}</div>
        <div>Umbral: {fmt(data.threshold)}</div>
        <div style={{ marginTop: 6 }}>Conserve este comprobante</div>
      </div>
    </div>
  );
}
